// src/hooks/useUserProfile.js
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../api/axios";
import { RE } from "../utils/system/regex";
import { formatPhoneKR } from "../utils/system/format";
import { normalizeMemberships } from "../utils/system/memberships";

export default function useUserProfile() {
  const { userId } = useParams();
  const isMyProfile = !userId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    username: "",
    email: "",
    phone_number: "",
    first_name: "",
    last_name: "",
  });

  const [org, setOrg] = useState({
    company_name: "",
    department_name: "",
    position_name: "",
  });

  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [passwords, setPasswords] = useState({
    current_password: "",
    password: "",
    confirmPassword: "",
  });

  const [signaturePreview, setSignaturePreview] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);

  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));
  const onChangePhone = (e) =>
    setForm((s) => ({ ...s, phone_number: formatPhoneKR(e.target.value) }));
  const onChangePw = (e) =>
    setPasswords((p) => ({ ...p, [e.target.name]: e.target.value }));

  const verifyCurrentPassword = async () => {
    try {
      await api.put("profile/", {
        current_password: passwords.current_password,
      });
      setPasswordVerified(true);
      alert("비밀번호 인증 성공");
    } catch {
      alert("현재 비밀번호가 올바르지 않습니다.");
    }
  };

  const submitPasswordChange = async () => {
    if (!RE.password.test(passwords.password)) return setError("형식 오류");
    if (passwords.password !== passwords.confirmPassword)
      return setError("새 비밀번호가 일치하지 않습니다.");

    await api.put("profile/", {
      current_password: passwords.current_password,
      password: passwords.password,
    });

    alert("비밀번호 변경 완료");
    setPasswords({ current_password: "", password: "", confirmPassword: "" });
    setPasswordVerified(false);
    setShowPasswordFields(false);
  };

  const validate = () => {
    if (!form.email || !form.first_name || !form.last_name)
      return setError("필수값 누락");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      email: form.email,
      phone_number: form.phone_number,
      first_name: form.first_name,
      last_name: form.last_name,
    };

    await api.put("profile/", payload);

    if (signatureFile) {
      const fd = new FormData();
      fd.append("file", signatureFile, "signature.png");
      const res = await api.post("me/signature/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSignaturePreview(res.data?.signature_url);
    }

    if (profileImageFile) {
      const fd2 = new FormData();
      fd2.append("profile_picture", profileImageFile);
      const res = await api.post("me/profile-picture/", fd2, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      console.log(res.data?.profile_picture_url);
      setProfileImagePreview(res.data?.profile_picture_url);
    }

    alert("저장 완료");
  };

  useEffect(() => {
    const loadData = async () => {
      const [{ data: profile }, { data: memRaw }] = await Promise.all([
        isMyProfile ? api.get("profile/") : api.get(`users/${userId}/`),
        isMyProfile
          ? api.get("membership/me/")
          : api.get(`users/${userId}/memberships/`),
      ]);

      const memberships = normalizeMemberships(memRaw);
      const primary =
        memberships.find((m) => m.is_primary) || memberships[0] || {};

      setOrg({
        company_name: primary.company_name || "",
        department_name: primary.department_name || "",
        position_name: primary.position_name || "",
      });

      setForm({
        username: profile.username || "",
        email: profile.email || "",
        phone_number: profile.phone_number || "",
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
      });

      setSignaturePreview(profile.sign_file);
      setProfileImagePreview(profile.profile_picture);
      setLoading(false);
    };

    loadData();
  }, [userId, isMyProfile]);

  return {
    loading,
    error,
    form,
    onChange,
    onChangePhone,
    onChangePw,
    org,
    showPasswordFields,
    setShowPasswordFields,
    passwordVerified,
    verifyCurrentPassword,
    passwords,
    submitPasswordChange,
    signaturePreview,
    setSignaturePreview,
    setSignatureFile,
    profileImagePreview,
    setProfileImagePreview,
    setProfileImageFile,
    isMyProfile,
    handleSubmit,
  };
}
